<template>
  <Bar 
    :chart-options="chartOptions"
    :chart-data="chartData"
    :chart-id="chartId"
    :dataset-id-key="datasetIdKey"
    :plugins="plugins"
    :css-classes="cssClasses"
    :styles="styles"
  ></Bar>
</template>

<script>
import { Bar } from 'vue-chartjs/legacy';


import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  BarElement,
  ArcElement,
  CategoryScale,
  LinearScale
} from 'chart.js'

ChartJS.register(Title, Tooltip, Legend, ArcElement, BarElement, CategoryScale, LinearScale)

const segmentText = (text, maxLength) => {
  if (text.length <= maxLength) {
    return [text]; // If the text is within the limit, return it as is.
  }

  const words = text.split(' '); // Split the input text into words.
  const segments = [];
  let currentSegment = '';

  for (const word of words) {
    if (currentSegment.length + word.length + 1 <= maxLength) {
      if (currentSegment) {
        currentSegment += ' '; // Add a space if it's not the first word in the segment.
      }
      currentSegment += word;
    } else {
      segments.push(currentSegment);
      currentSegment = word;
    }
  }

  if (currentSegment) {
    segments.push(currentSegment);
  }

  return segments;
};
const CHARACTER_COLUMN_LIMIT = 60;
const MINIMUM_PIXEL_HEIGHT = 140;

export default {
  components: {
    Bar
  },

  props: {
    records: {
      type: Array,
      required: true
    },
    chartId: {
      type: String,
      default: 'pie-chart'
    },
    datasetIdKey: {
      type: String,
      default: 'label'
    },
    cssClasses: {
      default: '',
      type: String
    },
    plugins: {
      type: Array,
      default: () => []
    }    
  },
  
  computed: {
    chartData() {
      return {
      labels: this.records.map(record => segmentText(record.name, CHARACTER_COLUMN_LIMIT)),
        datasets: [
          {
            data: this.records.map(record => record.funded_amount),
            backgroundColor: this.chartColors('dark'),
            hoverOffset: 10,
            label: this.$t('Funded amount'),
          },
          {
            data: this.records.map(record => (record.unfunded_amount)),
            backgroundColor: this.chartColors('light'),
            hoverOffset: 10,
            label: this.$t('Unfunded amount'),
          }
        ] 
      }
    },

    styles() {
      return {
        height: `${MINIMUM_PIXEL_HEIGHT + (this.records.length * 35)}px`,
        position: 'relative',
      }
    },

    chartOptions() {
      return {
        
        onClick: (e, activeEls) => this.handleClick(e, activeEls),
        onHover: (e, chartElement) => {
          e.native.target.style.cursor = chartElement[0] ? 'pointer' : 'default';
        },
        responsive: true,
        maintainAspectRatio: false,
        indexAxis: 'y',
        scaleShowValues: true,
        scales: {
          y: {
            ticks: { 
              autoSkip: false,
            },
            stacked: true,
          },
          x: { 
            stacked: true,
            ticks: {
              maxRotation: 45,
              minRotation: 45,
              callback: (value) => {
                return this.formatMoney(value);
              }
            }
          }
        },
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            mode: 'y',
            callbacks: {
              label: (context) => this.formatLabel(context),
            }
          }
        },
        layout: {
          padding: 15,
        }
      }
    }
  },

  methods: {
    chartColors(type) {
      // This function will automatically generate colors based on the number of records
      const colorArray = [];
      for (let i = 0; i < this.records.length; i++) {
        const hue = ((i + 1) * (360/this.records.length)) % 360;
        const color = `hsl(${hue}, 70%, ${(type==='dark') ? '50' : '85' }%)`;
        colorArray.push(color);
      }
      return colorArray;
    },
    
    formatMoney(val) {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0
 }).format(val);
    },

    formatLabel(context) {
      let label = context.dataset.label || '';
      if (label) {
        label += (process.env.VUE_APP_I18N_LOCALE=== 'en') ? ': ' : ' : ';
        if (context.parsed !== null) {
          label += this.formatMoney(context.parsed.x);
        }
        return label;
      }
    },

    handleClick(e, activeEls) {
      if (activeEls.length) {
        let dataIndex = activeEls[0].index;
        let datasetLabel = e.chart.data.labels[dataIndex];
        this.$emit('chartClick',datasetLabel);
      }
    }
  }
}

</script>

<style>
.chart_container {
  position: relative;
  margin: auto;
}
</style>
